/* Floating "View cart" bar.

   Sits at the bottom of every page (desktop, tablet and mobile) while
   the cart has something in it, showing:
     - a stack of the 3 MOST RECENTLY added products' images, oldest on
       the left and the newest one on the right (on top of the others).
       The cart itself is unlimited — only the stack is capped at 3, so
       adding a 4th product pushes the oldest thumbnail off the left of
       the stack while that product stays in the cart.
     - the total item count
     - a chevron button; clicking anywhere on the pill opens the site's
       existing cart drawer.

   Recency is worked out by diffing cart snapshots (a line item the
   previous snapshot did not have was just added; a quantity bump on a
   line already in the cart is not a new product and leaves the stack
   untouched), so it works for every add path on the site — product cards, the quick-add popup, the
   product page and the mobile product page alike — without each of them
   having to report anything. The resulting order is kept in
   sessionStorage so it survives navigating between pages.

   Styles live in view-cart-bar.css. */
(function () {
  'use strict';

  var MAX_THUMBS = 3;
  var STATE_KEY = 'animealViewCartBarState';
  var IMAGE_KEY = 'animealViewCartBarImages';
  // Bottom bars that own the bottom of the screen on some templates —
  // the pill lifts above whichever of these is actually visible.
  var STICKY_SELECTORS = ['.mpd-sticky', '.custom__floating_elem'];

  var bar = null;
  var thumbsEl = null;
  var countEl = null;
  var renderedKeys = [];
  var renderedSignature = '';
  var state = loadState(); // { order: [lineItemKey], qty: { key: qty } }
  var refreshTimer = null;
  var imageCache = loadImageCache();
  var offsetFrame = null;

  /* ---------------------------------------------------------------- *
   * State (persisted per browser tab, so the stack order survives a
   * page change — the cart does too, so the two must stay in sync)
   * ---------------------------------------------------------------- */

  function loadState() {
    try {
      var raw = sessionStorage.getItem(STATE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      if (parsed && Array.isArray(parsed.order) && parsed.qty) return parsed;
    } catch (e) {}
    return { order: [], qty: {} };
  }

  function loadImageCache() {
    try {
      var raw = sessionStorage.getItem(IMAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (e) {}
    return {};
  }

  function saveImageCache() {
    try {
      sessionStorage.setItem(IMAGE_KEY, JSON.stringify(imageCache));
    } catch (e) {}
  }

  function saveState() {
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  /* ---------------------------------------------------------------- *
   * Markup
   * ---------------------------------------------------------------- */

  function build() {
    if (bar) return bar;

    bar = document.createElement('button');
    bar.type = 'button';
    bar.className = 'view-cart-bar';
    bar.setAttribute('aria-label', 'View cart');
    bar.innerHTML =
      '<span class="view-cart-bar__thumbs" aria-hidden="true"></span>' +
      '<span class="view-cart-bar__text">' +
      '<span class="view-cart-bar__title">View cart</span>' +
      '<span class="view-cart-bar__count"></span>' +
      '</span>' +
      '<span class="view-cart-bar__arrow" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M9 5l7 7-7 7"/></svg>' +
      '</span>';

    thumbsEl = bar.querySelector('.view-cart-bar__thumbs');
    countEl = bar.querySelector('.view-cart-bar__count');
    bar.addEventListener('click', openCartDrawer);

    document.body.appendChild(bar);
    return bar;
  }

  /* ---------------------------------------------------------------- *
   * Opening the cart
   * ---------------------------------------------------------------- */

  function openCartDrawer() {
    if (bar) bar.classList.add('is-active');

    var openOptionsPopup = document.querySelector('[data-cvp-overlay]:not([hidden])');
    if (openOptionsPopup) {
      openOptionsPopup.setAttribute('hidden', '');
      document.body.classList.remove('cvp-open');
    }

    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(bar);
      return;
    }

    // Themes/templates without the drawer (cart_type = page) still have
    // the header cart link — let that do whatever it normally does.
    var cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.click();
      return;
    }

    window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  }

  /* ---------------------------------------------------------------- *
   * Cart snapshots -> recency order
   * ---------------------------------------------------------------- */

  // Identity of a cart line, stable across quantity changes.
  //
  // Shopify's own line "key" cannot be used here: on this store its hash
  // half is recomputed whenever the quantity changes (verified against
  // the live cart — 42344013529149:cb9b0c6b… became 42344013529149:c6256e3d…
  // after a +1), which made every quantity bump look like a brand new
  // product and sent it back to the front of the stack. The variant id
  // plus the line's own properties is stable and still tells two lines
  // of the same variant apart (e.g. one with a prescription attached).
  function itemKey(item, index) {
    var variant = item.variant_id || item.id;
    if (!variant) return String(item.key || index);
    return String(variant) + propertiesSignature(item);
  }

  function propertiesSignature(item) {
    var props = item.properties;
    if (!props || typeof props !== 'object') return '';
    var names = Object.keys(props)
      .filter(function (name) {
        return props[name] !== null && props[name] !== '' && props[name] !== undefined;
      })
      .sort();
    if (!names.length) return '';
    return (
      '|' +
      names
        .map(function (name) {
          return name + '=' + props[name];
        })
        .join('&')
    );
  }

  // Compares this snapshot with the previous one: only a line that is
  // NEW to the cart counts as "just added" and jumps to the front of the
  // stack (i.e. the right-hand end of the thumbnails). Bumping the
  // quantity of something already in the cart deliberately leaves the
  // stack alone — the product has not just joined the cart, it is only
  // getting more of itself. Lines that left the cart drop out entirely.
  // "state.order" is always newest-first; the rendering flips it so the
  // newest sits on the right.
  function applySnapshot(cart) {
    var items = (cart && cart.items) || [];
    var nextQty = {};
    var meta = {};
    var justAdded = [];

    items.forEach(function (item, index) {
      var key = itemKey(item, index);
      nextQty[key] = item.quantity;
      meta[key] = item;
      if (state.qty[key] === undefined) justAdded.push(key);
    });

    var isFirstSnapshot = !state.order.length && !Object.keys(state.qty).length;

    var order;
    if (isFirstSnapshot) {
      // No history yet (first page of the session, or the customer came
      // back to a cart built earlier). Shopify hands back the cart with
      // the most recently added line FIRST (verified against this
      // store's /cart.js), which is already the order this stack wants,
      // so take it as-is — reversing it here is what used to put the
      // newest product on the left.
      order = items.map(function (item, index) {
        return itemKey(item, index);
      });
    } else {
      // Walk the just-added lines oldest-first and push each to the
      // front, so the newest one (cart index 0) ends up frontmost even
      // when several arrive in the same snapshot.
      order = state.order.slice();
      justAdded
        .slice()
        .reverse()
        .forEach(function (key) {
          order = order.filter(function (existing) {
            return existing !== key;
          });
          order.unshift(key);
        });

      // Anything in the cart we somehow never saw (e.g. added in another
      // tab) goes to the back rather than being dropped.
      items.forEach(function (item, index) {
        var key = itemKey(item, index);
        if (order.indexOf(key) === -1) order.push(key);
      });
    }

    // Drop lines that are no longer in the cart.
    order = order.filter(function (key) {
      return Object.prototype.hasOwnProperty.call(nextQty, key);
    });

    state.order = order;
    state.qty = nextQty;
    saveState();

    render(order, meta, items);
  }

  function totalCount(items) {
    return items.reduce(function (sum, item) {
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  // Not every snapshot carries images. custom-atc.js keeps its own copy
  // of the cart and, after an add, that copy can be down to bare
  // { variant_id, quantity } rows — verified on the live store. Dropping
  // image-less rows outright made the whole stack disappear for a moment
  // and then rebuild itself, which is why every thumbnail used to fly in
  // again on each add. So remember the last image seen for each line and
  // fall back to it; only a line we have never seen an image for is
  // skipped.
  function imageUrl(item, key) {
    var src = item.image || (item.featured_image && item.featured_image.url) || '';
    if (!src) return key && imageCache[key] ? imageCache[key] : '';
    if (src.indexOf('width=') === -1) {
      src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=120';
    }
    if (key && imageCache[key] !== src) {
      imageCache[key] = src;
      saveImageCache();
    }
    return src;
  }

  /* ---------------------------------------------------------------- *
   * Rendering
   * ---------------------------------------------------------------- */

  function render(order, meta, items) {
    build();

    var count = totalCount(items);
    if (!count) {
      renderedKeys = [];
      renderedSignature = '';
      thumbsEl.innerHTML = '';
      setVisible(false);
      return;
    }

    var countText = count + (count === 1 ? ' Item' : ' Items');
    if (countEl.textContent !== countText) countEl.textContent = countText;

    // custom-atc.js optimistically drops a bare { variant_id, quantity }
    // row into its cart copy the moment an add succeeds, before the real
    // cart comes back. Those rows have no image yet, so skip them here
    // rather than flashing an empty white circle — the real row lands a
    // moment later and takes the same place in the stack.
    // "order" is newest-first; the stack is drawn oldest-first (left to
    // right) so the newest product ends up on the right, overlapping the
    // ones behind it.
    var keys = order
      .filter(function (key) {
        return !!imageUrl(meta[key] || {}, key);
      })
      .slice(0, MAX_THUMBS)
      .reverse();

    // Several events fire per add (optimistic copy, the real cart, the
    // drawer re-render). Re-running the DOM work for an identical stack
    // would restart the animations mid-flight, so bail out early when
    // nothing actually changed.
    var signature = keys.join('|');
    if (signature === renderedSignature) {
      setVisible(true);
      return;
    }
    var isFirstPaint = !renderedSignature;
    renderedSignature = signature;

    var existing = {};
    var leaving = {};
    Array.prototype.forEach.call(thumbsEl.children, function (node) {
      if (node.classList.contains('is-leaving')) leaving[node.dataset.key] = node;
      else existing[node.dataset.key] = node;
    });

    // A product that is still sliding out but has come back (a snapshot
    // arrived out of order, say) keeps its own element and simply drops
    // back into place — never a second element for the same product.
    keys.forEach(function (key) {
      var node = leaving[key];
      if (!node) return;
      window.clearTimeout(node.removalTimer);
      node.classList.remove('is-leaving');
      delete leaving[key];
      existing[key] = node;
    });

    // Reserve the slots first, so the pill settles into its new width
    // while the thumbnails move — never after them.
    thumbsEl.style.setProperty('--view-cart-bar-stack-count', String(Math.max(keys.length, 1)));

    // A product that dropped out of the top 3 (it is still in the cart —
    // only the stack is capped) leaves the way it arrived: straight up.
    // Nothing else in the pill reacts, because the slots are absolute.
    Object.keys(existing).forEach(function (key) {
      if (keys.indexOf(key) !== -1) return;
      var node = existing[key];
      node.classList.add('is-leaving');
      node.removalTimer = window.setTimeout(function () {
        if (node.parentNode) node.parentNode.removeChild(node);
      }, 400);
    });

    var arrivals = [];
    keys.forEach(function (key, slot) {
      var node = existing[key];
      if (!node) {
        node = document.createElement('span');
        node.className = 'view-cart-bar__thumb';
        node.dataset.key = key;
        var src = imageUrl(meta[key] || {}, key);
        if (src) {
          var img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          node.appendChild(img);
        }
        node.style.setProperty('--view-cart-bar-slot', String(slot));
        node.style.zIndex = String(slot + 1);
        // Park a genuine new arrival above its slot; it drops into place
        // on the next frame. A stack that was already in the cart when
        // the page loaded just appears, with no animation.
        if (!isFirstPaint) node.classList.add('is-entering');
        thumbsEl.appendChild(node);
        arrivals.push(node);
        return;
      }

      // Already on screen: just point it at its new slot and let the
      // transform transition glide it there. No reflow, no reordering of
      // DOM nodes, so nothing else in the pill moves.
      node.style.setProperty('--view-cart-bar-slot', String(slot));
      node.style.zIndex = String(slot + 1);
    });

    if (arrivals.length) {
      var drop = function () {
        arrivals.forEach(function (node) {
          node.classList.remove('is-entering');
        });
      };
      // Two frames: the first commits the parked position, the second
      // starts the drop. One frame alone can be collapsed by the browser
      // into a single style recalculation, which skips the animation.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(drop);
      });
      // Background tabs never run requestAnimationFrame, so the drop
      // would stay parked until the customer came back. Timers still
      // fire there, so use one as a safety net.
      window.setTimeout(drop, 250);
    }

    renderedKeys = keys;
    setVisible(true);
    updateOffset();
  }

  function isCartPage() {
    return document.body.classList.contains('template-cart');
  }

  function isDrawerOpen() {
    var drawer = document.querySelector('cart-drawer');
    return !!(drawer && drawer.classList.contains('active'));
  }

  function setVisible(show) {
    if (!bar) return;
    var visible = show && !isCartPage() && !isDrawerOpen();
    bar.classList.toggle('is-visible', visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    bar.tabIndex = visible ? 0 : -1;
  }

  function refreshVisibility() {
    if (!bar) return;
    setVisible(renderedKeys.length > 0);
    if (!isDrawerOpen()) bar.classList.remove('is-active');
  }

  // Lifts the pill above whichever sticky bottom bar this template
  // happens to be showing (the mobile product page's Add to cart bar,
  // the desktop product page's floating button) so they never overlap.
  function updateOffset() {
    if (!bar) return;
    if (offsetFrame) window.cancelAnimationFrame(offsetFrame);
    offsetFrame = window.requestAnimationFrame(function () {
      offsetFrame = null;
      var tallest = 0;
      STICKY_SELECTORS.forEach(function (selector) {
        document.querySelectorAll(selector).forEach(function (el) {
          if (!el.offsetParent && el.offsetHeight === 0) return;
          var styles = window.getComputedStyle(el);
          if (styles.display === 'none' || styles.visibility === 'hidden') return;
          if (styles.position !== 'fixed') return;
          tallest = Math.max(tallest, el.getBoundingClientRect().height);
        });
      });

      if (tallest > 0) {
        bar.classList.add('view-cart-bar--raised');
        bar.style.bottom = Math.round(tallest) + 12 + 'px';
      } else {
        bar.classList.remove('view-cart-bar--raised');
        bar.style.bottom = '';
      }
    });
  }

  /* ---------------------------------------------------------------- *
   * Cart data plumbing
   * ---------------------------------------------------------------- */

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        applySnapshot(cart);
      })
      .catch(function () {});
  }

  function scheduleRefresh(delay) {
    if (refreshTimer) window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(function () {
      refreshTimer = null;
      fetchCart();
    }, delay || 120);
  }

  // Some events hand us the cart they just fetched — use it and skip the
  // extra network round trip. Anything without item data triggers a
  // (debounced) refetch instead.
  function handleCartEvent(event) {
    var cart = event && event.detail && (event.detail.cart || event.detail.cartData);
    if (cart && Array.isArray(cart.items)) {
      applySnapshot(cart);
      return;
    }
    scheduleRefresh();
  }

  function watchDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(refreshVisibility).observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  function watchStickyBars() {
    STICKY_SELECTORS.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        new MutationObserver(updateOffset).observe(el, {
          attributes: true,
          attributeFilter: ['class', 'style'],
        });
      });
    });
  }

  function init() {
    build();

    // custom-atc.js keeps its own always-fresh copy of the cart and
    // announces every change — that's the main feed. The others cover
    // add/remove paths that don't go through it.
    window.addEventListener('animeal:cart:state', handleCartEvent);
    window.addEventListener('animeal:cart:changed', handleCartEvent);
    window.addEventListener('animeal:cart-drawer:rendered', handleCartEvent);
    window.addEventListener('custom-atc-updated', function () {
      scheduleRefresh();
    });
    document.addEventListener('cart:updated', function () {
      scheduleRefresh();
    });
    document.addEventListener('custom-cart-update', function () {
      scheduleRefresh();
    });
    if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () {
        scheduleRefresh();
      });
    }

    window.addEventListener('resize', updateOffset);
    window.addEventListener('scroll', updateOffset, { passive: true });
    window.addEventListener('pageshow', function () {
      scheduleRefresh(0);
    });

    watchDrawer();
    watchStickyBars();

    var known = window.customATCManager && window.customATCManager.cartData;
    if (known && Array.isArray(known.items)) {
      applySnapshot(known);
    }
    fetchCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
